import { apiClient } from './config.js';

export interface TriagePick {
  candidate_id: string;
  rank: number | null;
  score: number | null;
  action: string | null;
  target_id: string | null;
  target_name: string | null;
  article_ids: string[];
}

export interface TriageArticle {
  article_id: string;
  announcement_id: string | null;
  title: string;
  publish_status: string | null;
  is_secondary: boolean;
  created_at: string;
}

export interface TriageRow {
  scan_id: string;
  scan_date: string;
  ticker: string;
  title: string;
  announcement_time: string | null;
  pdf_url: string | null;
  price_sensitive: boolean;
  skipped_fast_lane: boolean;
  stage1_route: string | null;
  stage1_signal_type: string | null;
  stage1_direction: string | null;
  stage2_sector: string | null;
  stage2_content_type: string | null;
  stage2_score: number | null;
  stage2_rationale: string | null;
  discovery_picked_at: string | null;
  operator_action: 'promote' | 'demote' | null;
  operator_action_at: string | null;
  operator_action_by: string | null;
  operator_action_reason: string | null;
  operator_promote_job_id: string | null;
  entity_id: string | null;
  entity_ticker: string | null;
  entity_name: string | null;
  entity_discovery_status: string | null;
  signal_id: string | null;
  signal_db_type: string | null;
  signal_label: string | null;
  signal_original_type: string | null;
  picks: TriagePick[];
  articles: TriageArticle[];
}

export interface TriageSummary {
  scan_date: string;
  total_rows: number;
  stage1_routes: Record<string, number>;
  stage2_content_types: Record<string, number>;
  fast_lane_skipped: number;
  discovery_picked: number;
  with_signal: number;
  with_article: number;
  signal_routed: number;
  signal_written: number;
  signal_route_unwritten: number;
  blocklist_drops: number;
  promoted_by_operator: number;
  demoted_by_operator: number;
}

export interface TriageDateWindow {
  start: string;
  end: string;
  trading_days: string[];
}

export interface TriagePerDayBucket {
  total_rows: number;
  discovery_picked: number;
  fast_lane_skipped: number;
  with_signal: number;
  with_article: number;
}

export interface MacroContextBlock {
  snapshot_at: string | null;
  priority_sectors: string[];
  macro_themes: string[];
  sector_returns: Record<string, number> | null;
  commodities: Record<string, { name?: string; price?: number | null; day_change_pct?: number | null }> | null;
}

export interface ActiveTheme {
  theme_id: string;
  title: string;
  summary: string | null;
  sectors: string[];
  commodity_tags: string[];
  importance: number | null;
  momentum: string;
  horizon: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
}

export interface TriageTodayResponse {
  summary: TriageSummary;
  rows: TriageRow[];
  exchange: string;
  date_window: TriageDateWindow;
  per_day: Record<string, TriagePerDayBucket>;
  macro_context: MacroContextBlock | null;
  active_themes: ActiveTheme[];
}

export interface TriageTodayParams {
  exchange?: string;
  days?: number;
  scanDate?: string;
  limit?: number;
}

export async function getTriageToday(
  params: TriageTodayParams = {},
): Promise<TriageTodayResponse> {
  const search = new URLSearchParams();
  search.set('exchange', params.exchange ?? 'ASX');
  search.set('days', String(params.days ?? 2));
  if (params.scanDate) search.set('scan_date', params.scanDate);
  search.set('limit', String(params.limit ?? 2000));
  const response = await apiClient.get(`/admin/triage/today?${search}`);
  const data = response.data as TriageTodayResponse;
  // Normalize jsonb columns. asyncpg returns jsonb as a string unless a
  // type codec is set on the pool — do the parse client-side so the rest
  // of the UI can rely on .map / .every / .some.
  if (Array.isArray(data?.rows)) {
    for (const row of data.rows) {
      row.articles = coerceJsonArray<TriageArticle>(row.articles);
      row.picks = coerceJsonArray<TriagePick>(row.picks);
    }
  }
  return data;
}

function coerceJsonArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export interface PromoteResponse {
  scan_id: string;
  status:
    | 'queued'
    | 'reprocessing'
    | 'noop_already_has_articles'
    | 'noop_already_promoted';
  announcement_id: string | null;
  job_id: string | null;
  article_ids: string[];
  message: string;
}

export async function promoteScan(
  scanId: string,
  reason?: string,
): Promise<PromoteResponse> {
  const response = await apiClient.post(
    `/admin/triage/scans/${scanId}/promote`,
    { reason },
  );
  return response.data;
}

export interface DemoteResponse {
  announcement_id: string;
  status: 'demoted' | 'noop';
  demoted_to_draft: number;
  already_draft: number;
  skipped_archived: number;
  unpublish_jobs_queued: number;
  message: string;
}

export interface DemoteRequest {
  unpublish: boolean;
  reason?: string;
}

export async function demoteAnnouncement(
  announcementId: string,
  body: DemoteRequest,
): Promise<DemoteResponse> {
  const response = await apiClient.post(
    `/admin/triage/announcements/${announcementId}/demote`,
    body,
  );
  return response.data;
}

// =============================================================================
// Track 4b — Held articles + deep-backsource events
// =============================================================================

export interface HeldEventSummary {
  event_id: string;
  primary_announcement_id: string;
  primary_announcement_title: string | null;
  primary_company_code: string | null;
  activity_sub_type: string;
  state: 'pending' | 'saturating' | 'terminal';
  gate_outcome:
    | 'completed'
    | 'budget_exhausted'
    | 'circuit_breaker'
    | 'operator_released'
    | 'failed'
    | null;
  budget_max: number;
  budget_used: number;
  saturation_jobs_total: number;
  saturation_jobs_done: number;
  counterparty_count: number;
  held_article_count: number;
  operator_released_by: string | null;
  operator_released_at: string | null;
  last_progress_at: string | null;
  created_at: string;
  minutes_since_last_progress: number | null;
}

export interface HeldEventListResponse {
  events: HeldEventSummary[];
  total: number;
}

export interface OperatorReleaseResponse {
  event_id: string;
  state: string;
  gate_outcome: string | null;
  held_articles_released: number;
  message: string;
}

export async function listHeldEvents(
  includeTerminal: boolean = false,
): Promise<HeldEventListResponse> {
  const search = new URLSearchParams();
  if (includeTerminal) search.set('include_terminal', 'true');
  const response = await apiClient.get(`/admin/triage/held-events?${search}`);
  return response.data as HeldEventListResponse;
}

export async function releaseHeldEvent(
  eventId: string,
  reason?: string,
): Promise<OperatorReleaseResponse> {
  const response = await apiClient.post(
    `/admin/triage/held-events/${eventId}/release`,
    { reason },
  );
  return response.data as OperatorReleaseResponse;
}

// ============================================================================
// Selected stories — Phase 2 operator visibility
// ============================================================================

export interface SelectedSource {
  item_id: string;
  announcement_id: string;
  role: string | null;
  title: string;
  doc_form: string | null;
  editorial_substance: number | null;
  activity_sub_type: string | null;
  editorial_category: string | null;
  announcement_date: string;
  source_name: string | null;
}

export interface SelectedItem {
  item_id: string;
  plan_id: string;
  action: string;
  is_secondary: boolean;
  triage_rationale: string | null;
  plan_created_at: string;
  primary_announcement_id: string;
  generated_article_id: string | null;
  entity_id: string | null;
  entity_company_code: string | null;
  entity_company_name: string | null;
  primary_title: string;
  primary_doc_form: string | null;
  primary_editorial_substance: number | null;
  primary_activity_sub_type: string | null;
  product_id: string;
  product_name: string;
  /**
   * Content product route type. Drives the Triage anchor badge so
   * substance=1 procedural items routed to `intelligence_signal` are
   * visibly tagged as "signal source" — not "editorial anchor" —
   * regardless of whether the source bundle hangs off a plan item.
   * Known values: 'article' (editorial mainline), 'intelligence_signal'
   * (signal cards on /intelligence/), 'activity_item' (Activity Feed
   * 250-400 word briefs), 'synthesis' (Pre-Market, Sector Wrap, etc.).
   */
  product_type: string | null;
  target_name: string | null;
  persona_slug: string | null;
  article_id: string | null;
  article_title: string | null;
  article_publish_status: string | null;
  article_rewrite_count: number;
  article_auto_published: boolean;
  article_edited: boolean;
  sources: SelectedSource[];
}

export interface SelectedResponse {
  items: SelectedItem[];
  count: number;
  target_date: string;
}

export async function getTriageSelected(targetDate?: string): Promise<SelectedResponse> {
  const params = targetDate ? { params: { target_date: targetDate } } : {};
  const response = await apiClient.get('/admin/triage/selected', params);
  return response.data as SelectedResponse;
}

/**
 * T-4 — the COVERAGE path's per-filing chain.
 *
 * The broad-scan path has `announcement_scans` and the triage console. The
 * coverage path has neither: `is_fast_lane` groups make the broad scan SKIP
 * their members, so a paying customer's filings never appear in the surface
 * built to show what happened to a filing. The least observable filings in the
 * system are the ones that are paid for — which is why the 2026-08-27 AQN
 * investigation had to be done in SQL and Cloud Logging rather than here.
 */
export interface CoverageChainArticle {
  target_name: string;
  product_name: string;
  article_id: string;
  publish_status: string | null;
  /** LIVE on main. `publish_status='published'` means on TEST, not live. */
  is_live: boolean;
}

export interface CoverageChainRow {
  announcement_id: string;
  company_code: string;
  announcement_title: string;
  created_at: string;
  price_sensitive: boolean;
  content_types: string[] | null;
  /** The announcement's COMMODITY axis. */
  announcement_sector: string | null;
  /** The entity's OWNERSHIP axis (CT-3). Divergence caused the AQN miss. */
  entity_sector: string | null;
  editorial_category: string | null;
  activity_sub_type: string | null;
  coverage_group: string;
  /** T-1 (`routing_decisions`) — null for filings predating migration 326. */
  rules_evaluated: number | null;
  matched_count: number | null;
  targets_matched: string[] | null;
  backstop_applied: boolean | null;
  /** T-2 (`plan_skips`) — set when the planner deliberately declined. */
  skip_reason: string | null;
  skip_detail: string | null;
  superseded_by_announcement_id: string | null;
  articles: CoverageChainArticle[];
  live_mastheads: number;
  active_mastheads: number;
  /** Precomputed operator verdict. */
  outcome: 'complete' | 'partial' | 'no_article' | 'declined';
}

export interface CoverageChainResponse {
  days: number;
  count: number;
  outcomes: Record<string, number>;
  items: CoverageChainRow[];
}

export async function getCoverageChain(
  params: { days?: number; limit?: number } = {},
): Promise<CoverageChainResponse> {
  const search = new URLSearchParams();
  if (params.days) search.set('days', String(params.days));
  if (params.limit) search.set('limit', String(params.limit));
  const response = await apiClient.get(`/admin/triage/coverage?${search}`);
  return response.data;
}
